import type React from "react";
import { fmtInt, fmtPct } from "@/lib/format";
import type { Slide, SlideContext } from "../slide.types";
import { Tile } from "../visuals/Tile";
import { Card, CardLabel } from "../visuals/Card";
import { TopBars, type TopBarItem } from "../visuals/TopBars";
import { ACCENT_RED, fleetIdleRateAccent } from "../colors";

function InactiveTeamsSlideView({ data, resetKey }: SlideContext): React.JSX.Element {
  const stores = data.methodology.stores;

  const completelyInactive = stores.filter((s) => s.sessions === 0);
  const idleAtActive = stores.filter((s) => s.sessions > 0 && s.idle_devices > 0);

  const totalIdleDevices = idleAtActive.reduce((sum, s) => sum + s.idle_devices, 0);
  const avgIdleRate =
    idleAtActive.length > 0
      ? idleAtActive.reduce((sum, s) => sum + s.fleet_idle_rate, 0) / idleAtActive.length
      : 0;

  if (completelyInactive.length === 0 && idleAtActive.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/40 text-sm font-body">
          No inactive teams or idle devices this period.
        </p>
      </div>
    );
  }

  const topIdle: TopBarItem[] = [...idleAtActive]
    .sort((a, b) => b.fleet_idle_rate - a.fleet_idle_rate)
    .slice(0, 5)
    .map((s) => ({
      label: s.store,
      value: s.fleet_idle_rate,
      display: fmtPct(s.fleet_idle_rate),
    }));

  const inactiveList = [...completelyInactive].sort((a, b) => b.devices_total - a.devices_total);
  const shownInactive = inactiveList.slice(0, 8);
  const hiddenInactiveCount = inactiveList.length - shownInactive.length;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <Tile
          label="Completely Inactive Teams"
          value={completelyInactive.length}
          format={(v) => fmtInt(Math.round(v))}
          delay={100}
          {...(completelyInactive.length > 0 ? { accent: ACCENT_RED } : {})}
          resetKey={resetKey}
        />
        <Tile
          label="Idle Devices at Active Teams"
          value={totalIdleDevices}
          format={(v) => fmtInt(Math.round(v))}
          delay={200}
          resetKey={resetKey}
        />
        <Tile
          label="Avg Fleet Idle Rate"
          value={avgIdleRate}
          format={(v) => `${v.toFixed(1)}%`}
          delay={300}
          accent={fleetIdleRateAccent(avgIdleRate)}
          resetKey={resetKey}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {topIdle.length > 0 ? (
          <TopBars
            title="Highest Idle Rate — Active Teams"
            items={topIdle}
            delay={400}
            accentTop={ACCENT_RED}
          />
        ) : (
          <Card delay={400}>
            <CardLabel>Highest Idle Rate — Active Teams</CardLabel>
            <p className="text-white/40 text-sm font-body mt-6">
              No idle devices at otherwise-active teams this period.
            </p>
          </Card>
        )}

        <Card delay={500}>
          <CardLabel>Completely Inactive Teams</CardLabel>
          {shownInactive.length === 0 ? (
            <p className="text-white/40 text-sm font-body mt-6">
              No completely inactive teams this period.
            </p>
          ) : (
            <div className="space-y-2.5">
              {shownInactive.map((team, i) => (
                <div
                  key={team.store}
                  className="flex items-center justify-between"
                  style={{ animation: `pmFade 500ms ease-out ${550 + i * 40}ms both` }}
                >
                  <span className="text-white/80 text-sm font-body truncate pr-3">{team.store}</span>
                  <span className="text-white/40 text-xs font-body whitespace-nowrap">
                    {fmtInt(team.devices_total)} devices
                  </span>
                </div>
              ))}
              {hiddenInactiveCount > 0 ? (
                <p className="text-white/25 text-[11px] font-body uppercase tracking-widest pt-2">
                  +{hiddenInactiveCount} more
                </p>
              ) : null}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

export const inactiveTeamsSlide: Slide = {
  id: "inactive-teams",
  title: "Inactive Teams",
  render: (ctx) => <InactiveTeamsSlideView {...ctx} />,
};
